/**
 * Portal Subject API Client
 *
 * Handles operations for subject management with Cognito authentication.
 * Subjects are organized hierarchically with parent-child relationships.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to subject endpoint.
 */
const subjectApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/subject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List all subjects with hierarchical structure.
 *
 * @returns {Promise<Array>} Array of parent subjects with children:
 * [
 *   {
 *     id: "uuid",
 *     subjectName: "English",
 *     sortOrder: 1,
 *     children: [
 *       { id: "uuid", subjectName: "Reading", sortOrder: 1 },
 *       { id: "uuid", subjectName: "Writing", sortOrder: 2 }
 *     ]
 *   },
 *   ...
 * ]
 */
export const listSubjects = async () => {
  const result = await subjectApiRequest('list');
  return result.subjects || [];
};

/**
 * Get a single subject by ID.
 *
 * @param {string} subjectId - Subject ID
 * @returns {Promise<Object>} Subject object with children if parent
 */
export const getSubject = async (subjectId) => {
  const result = await subjectApiRequest('get', { id: subjectId });
  return result.subject;
};

/**
 * Helper: Flatten subjects into a simple list for single-select dropdowns.
 * Returns only parent subjects (no children).
 *
 * @param {Array} subjects - Hierarchical subjects array
 * @returns {Array} Flat array of { id, subjectName }
 */
export const flattenParentSubjects = (subjects) => {
  return subjects.map(s => ({
    id: s.id,
    subjectName: s.subjectName
  }));
};

/**
 * Helper: Get all subjects (parent and children) as a flat list.
 * Useful for material management where only one subject is selected.
 *
 * @param {Array} subjects - Hierarchical subjects array
 * @returns {Array} Flat array of { id, subjectName, parentId, parentName }
 */
export const flattenAllSubjects = (subjects) => {
  const result = [];
  for (const parent of subjects) {
    // Add parent subject
    result.push({
      id: parent.id,
      subjectName: parent.subjectName,
      parentId: null,
      parentName: null,
      isParent: true
    });
    // Add child subjects
    for (const child of (parent.children || [])) {
      result.push({
        id: child.id,
        subjectName: child.subjectName,
        parentId: parent.id,
        parentName: parent.subjectName,
        isParent: false
      });
    }
  }
  return result;
};

/**
 * Helper: Format subjects for display in course group cards.
 * Example output: "English (Reading, Writing), Mathematics"
 *
 * @param {Array} subjects - Course group's subjects array
 * @returns {string} Formatted display string
 */
export const formatSubjectsDisplay = (subjects) => {
  if (!subjects || subjects.length === 0) {
    return '-';
  }

  return subjects.map(subject => {
    const childNames = (subject.children || []).map(c => c.subjectName);
    if (childNames.length > 0) {
      return `${subject.subjectName} (${childNames.join(', ')})`;
    }
    return subject.subjectName;
  }).join(', ');
};

/**
 * Helper: Get subject names as a simple comma-separated list.
 * Only includes parent subject names.
 *
 * @param {Array} subjects - Course group's subjects array
 * @returns {string} Comma-separated subject names
 */
export const getSubjectNames = (subjects) => {
  if (!subjects || subjects.length === 0) {
    return '';
  }
  return subjects.map(s => s.subjectName).join(', ');
};

// Cache for subjects to avoid repeated API calls
let subjectsCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * List subjects with caching.
 *
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Array>} Hierarchical subjects array
 */
export const listSubjectsCached = async (forceRefresh = false) => {
  const now = Date.now();

  if (!forceRefresh && subjectsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_TTL)) {
    return subjectsCache;
  }

  subjectsCache = await listSubjects();
  cacheTimestamp = now;
  return subjectsCache;
};
